import { Box, CircularProgress, Alert, Paper, useTheme } from '@mui/material';
import { useBpmnViewer } from '../../hooks/useBpmnViewer';
import { BpmnViewerToolbar } from './BpmnViewerToolbar';
import type { BpmnViewerProps } from '../../types/bpmn.types';

import 'bpmn-js/dist/assets/diagram-js.css';
import 'bpmn-js/dist/assets/bpmn-font/css/bpmn.css';

// Dark mode styles for BPMN viewer
const darkModeStyles = {
  '& .djs-container': {
    backgroundColor: '#1e1e1e !important',
  },
  '& .djs-visual > *': {
    stroke: '#e0e0e0 !important',
  },
  '& .djs-visual text': {
    fill: '#e0e0e0 !important',
  },
  '& .djs-connection path': {
    stroke: '#90caf9 !important',
  },
  '& .djs-connection marker path': {
    fill: '#90caf9 !important',
    stroke: '#90caf9 !important',
  },
  '& .djs-shape .djs-visual > rect, & .djs-shape .djs-visual > circle, & .djs-shape .djs-visual > polygon': {
    fill: '#2d2d2d !important',
  },
  '& .djs-outline': {
    stroke: '#90caf9 !important',
  },
};

export function BpmnViewer({
  processDefinitionKey,
  processInstanceId,
  showExecutionState = true,
  height = 500,
}: BpmnViewerProps) {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const {
    containerRef,
    isLoading,
    error,
    zoomIn,
    zoomOut,
    fitViewport,
    exportSvg,
    exportPng,
  } = useBpmnViewer({
    processDefinitionKey,
    processInstanceId,
    showExecutionState,
  });

  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Paper
      variant="outlined"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: typeof height === 'number' ? height : height,
        overflow: 'hidden',
      }}
    >
      <BpmnViewerToolbar
        onZoomIn={zoomIn}
        onZoomOut={zoomOut}
        onFitViewport={fitViewport}
        onExportSvg={exportSvg}
        onExportPng={exportPng}
        processDefinitionKey={processDefinitionKey}
      />

      <Box
        sx={{
          flex: 1,
          position: 'relative',
          bgcolor: isDarkMode ? 'grey.900' : 'grey.50',
        }}
      >
        {isLoading && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: isDarkMode ? 'rgba(30, 30, 30, 0.8)' : 'rgba(255, 255, 255, 0.8)',
              zIndex: 1,
            }}
          >
            <CircularProgress />
          </Box>
        )}

        <Box
          ref={containerRef}
          sx={{
            width: '100%',
            height: '100%',
            '& .bjs-powered-by': {
              display: 'none',
            },
            ...(isDarkMode && darkModeStyles),
          }}
        />
      </Box>
    </Paper>
  );
}
